import StudioBadgeSvg from '@components/Images/StudioBadgeSvg';
import {
  PROFILE_CLIENT_PROJECTS_LAYOUT_TYPE,
  ProfileClientProjectsLayoutType,
} from '@utils/constants';
import { fillProfileUrl } from '@utils/urlUtils';
import { PortfolioItem } from '@wix/ambassador-partners-profile-v1-partner-profile/types';
import { Box, Image, MediaOverlay, Proportion, Text } from '@wix/design-system';
import { useMemo, useState, useEffect, useRef } from 'react';
import s from './Project.module.scss';

export interface PortfolioItemWithUrl extends PortfolioItem {
  url: string;
  isStudio?: boolean;
  isMobileView?: boolean;
  layoutType?: ProfileClientProjectsLayoutType;
  onProjectClick?: (project: PortfolioItemWithUrl) => void;
}

export const generateThumbnailUrl = (
  metaSiteId: string,
  w: number,
  h: number,
) => `https://www.wix.com/site-thumbnail/${metaSiteId}?w=${w}&h=${h}`;

const checkImageLoadState = (
  containerRef: React.RefObject<HTMLElement>,
  onImageLoaded: () => void,
) => {
  const img = containerRef.current?.querySelector('img');
  if (!img) return;

  if (img.complete && img.naturalWidth > 0) {
    onImageLoaded();
  } else {
    img.onload = () => onImageLoaded();
  }
};

const Project = ({
  customImage,
  description,
  metaSiteId,
  siteName,
  url,
  isStudio,
  isMobileView,
  layoutType = 'threeColumn',
  ...projectData
}: PortfolioItemWithUrl) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const imageBoxRef = useRef<HTMLDivElement>(null);

  const mediaSources = useMemo(() => {
    const dimensions =
      layoutType === PROFILE_CLIENT_PROJECTS_LAYOUT_TYPE.TWO_COLUMN
        ? { width: 654, height: 383 }
        : { width: 429, height: 251 };

    const generatedThumbnailSrc = metaSiteId
      ? generateThumbnailUrl(metaSiteId, dimensions.width, dimensions.height)
      : '';

    const mediaSource = customImage
      ? fillProfileUrl(customImage, dimensions.width, dimensions.height)
      : generatedThumbnailSrc;

    const backupSource = customImage ? generatedThumbnailSrc : '';
    return { mediaSource, backupSource };
  }, [metaSiteId, customImage, layoutType]);

  useEffect(() => {
    setImageLoaded(false);
    checkImageLoadState(imageBoxRef, () => setImageLoaded(true));
  }, [mediaSources.mediaSource]);

  return (
    <Box
      dataHook="portfolio-project"
      className={isMobileView ? s.projectMobile : s.project}
    >
      <Proportion aspectRatio={1.79} className={s.proportion}>
        <MediaOverlay
          hoverSkin="gradient"
          borderRadius="3px 3px 0 0"
          className={s.mediaOverlay}
          media={
            <Box height="100%" className={s.imageWrapper} ref={imageBoxRef}>
              <Image
                className={`${s.projectImage} ${imageLoaded ? s.loaded : ''}`}
                fit="cover"
                dataHook="project-media-image"
                alt={`${siteName}: ${description}`}
                borderRadius="3px 3px 0 0"
                src={mediaSources.mediaSource}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  if (mediaSources.backupSource) {
                    e.currentTarget.src = mediaSources.backupSource;
                    setImageLoaded(false); // Reset for backup image
                  }
                }}
              />
              {isStudio ? (
                <Box
                  dataHook="project-studio-badge"
                  position="absolute"
                  right="6px"
                  bottom="6px"
                  zIndex="1"
                >
                  <StudioBadgeSvg />
                </Box>
              ) : null}
            </Box>
          }
          dataHook="project-media-overlay"
        >
          <MediaOverlay.Content
            placement="bottom-start"
            visible="hover"
            data-hook="project-media-overlay-content"
          >
            <Box className={s.hoverContent}>
              <Text size="small" weight="normal" className={s.projectName}>
                {siteName}
              </Text>
            </Box>
          </MediaOverlay.Content>
        </MediaOverlay>
      </Proportion>
    </Box>
  );
};

export default Project;
